/**
 * Pending Approvals Page
 * Action-focused page: force clock-out stuck sessions, correct times, approve pending entries.
 * For full history, corrections on approved entries, and manual entry — use Time Entries.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { UserProfile, TimeEntry } from '../../../shared/types';
import { useAuth } from '../hooks/useAuth';
import { getManagerStaff, getCompanyUsers } from '../services/user.service';
import { approveTimeEntry, correctTimeEntry, bulkApproveTimeEntries } from '../services/timeEntry.service';
import { convertDocumentTimestamps } from '@shared/utils/firestore';
import {
  StatCard,
  DashboardGrid,
  LoadingState,
  QuickActionButton
} from '../components/dashboard';
import { PageHeader, Toast } from '../components/common';
import { COLORS, SPACING, TYPOGRAPHY, BORDER_RADIUS } from '../constants/theme';
import { useToast } from '../hooks/useToast';

interface CorrectionModalState {
  entryId: string;
  staffName: string;
  originalClockIn: Date;
  originalClockOut: Date | null;
  isActive: boolean;
}

interface ApproveConfirmState {
  entryId: string;
  staffName: string;
  hours: number;
}

interface BulkApproveConfirmState {
  entryIds: string[];
  totalHours: number;
}

const toLocalDatetimeInput = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export function PendingApprovals() {
  const { currentUser } = useAuth();
  const tz = currentUser?.companyTimezone;
  const { toasts, showToast, dismissToast } = useToast();
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [activeEntries, setActiveEntries] = useState<TimeEntry[]>([]);
  const [pendingEntries, setPendingEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [approveConfirm, setApproveConfirm] = useState<ApproveConfirmState | null>(null);
  const [bulkApproveConfirm, setBulkApproveConfirm] = useState<BulkApproveConfirmState | null>(null);
  const [correctionModal, setCorrectionModal] = useState<CorrectionModalState | null>(null);
  const [correctionClockIn, setCorrectionClockIn] = useState('');
  const [correctionClockOut, setCorrectionClockOut] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const gracePeriodMinutes = currentUser?.gracePeriodMinutes ?? 5;

  const loadStaff = async () => {
    if (!currentUser?.uid || !currentUser?.companyId) return;
    try {
      const staffMembers = await (currentUser.userType === 'company'
        ? getCompanyUsers(currentUser.companyId)
        : currentUser.userType === 'manager'
          ? getManagerStaff(currentUser.uid)
          : Promise.resolve([] as UserProfile[]));
      setStaff(staffMembers);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  useEffect(() => {
    if (currentUser?.uid) loadStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // Real-time listener for active entries
  useEffect(() => {
    if (!currentUser?.companyId) return;

    const activeQuery = currentUser.userType === 'manager'
      ? query(collection(db, 'TimeEntries'), where('managerId', '==', currentUser.uid), where('status', '==', 'active'))
      : query(collection(db, 'TimeEntries'), where('companyId', '==', currentUser.companyId), where('status', '==', 'active'));

    return onSnapshot(activeQuery, (snapshot) => {
      const entries = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...convertDocumentTimestamps<TimeEntry>(doc.data()),
      })) as TimeEntry[];
      entries.sort((a, b) => a.actualClockIn.getTime() - b.actualClockIn.getTime());
      setActiveEntries(entries);
    });
  }, [currentUser?.companyId, currentUser?.uid, currentUser?.userType]);

  // Real-time listener for pending entries
  useEffect(() => {
    if (!currentUser?.companyId) {
      setLoading(false);
      return;
    }

    const pendingQuery = currentUser.userType === 'manager'
      ? query(collection(db, 'TimeEntries'), where('managerId', '==', currentUser.uid), where('status', '==', 'pending'))
      : query(collection(db, 'TimeEntries'), where('companyId', '==', currentUser.companyId), where('status', '==', 'pending'));

    return onSnapshot(pendingQuery, (snapshot) => {
      const entries = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...convertDocumentTimestamps<TimeEntry>(doc.data()),
      })) as TimeEntry[];
      entries.sort((a, b) => b.actualClockIn.getTime() - a.actualClockIn.getTime());
      setPendingEntries(entries);
      setLoading(false);
    }, (error) => {
      console.error('PendingApprovals snapshot error:', error);
      setLoading(false);
    });
  }, [currentUser?.companyId, currentUser?.uid, currentUser?.userType]);

  const openApproveConfirm = (entry: TimeEntry) => {
    const staffMember = staffMap.get(entry.userId);
    setApproveConfirm({
      entryId: entry.id,
      staffName: staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'Unknown',
      hours: entry.paidHours,
    });
  };

  const confirmApproveEntry = async () => {
    if (!approveConfirm) return;
    try {
      await approveTimeEntry(approveConfirm.entryId);
      setApproveConfirm(null);
    } catch (error) {
      console.error('Error approving entry:', error);
      showToast('Failed to approve time entry', 'error');
    }
  };

  const openBulkApproveConfirm = () => {
    if (selectedEntries.size === 0) return;
    const totalHours = filteredPendingEntries
      .filter(e => selectedEntries.has(e.id))
      .reduce((sum, e) => sum + e.paidHours, 0);
    setBulkApproveConfirm({ entryIds: Array.from(selectedEntries), totalHours });
  };

  const confirmBulkApprove = async () => {
    if (!bulkApproveConfirm) return;
    try {
      await bulkApproveTimeEntries(bulkApproveConfirm.entryIds);
      setSelectedEntries(new Set());
      setBulkApproveConfirm(null);
    } catch (error) {
      console.error('Error bulk approving:', error);
      showToast('Failed to approve time entries', 'error');
    }
  };

  const openCorrectionModal = (entry: TimeEntry, isActive = false) => {
    const staffMember = staffMap.get(entry.userId);
    setCorrectionModal({
      entryId: entry.id,
      staffName: staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'Unknown',
      originalClockIn: entry.actualClockIn,
      originalClockOut: entry.actualClockOut ?? null,
      isActive,
    });
    setCorrectionClockIn(toLocalDatetimeInput(entry.actualClockIn));
    setCorrectionClockOut(isActive ? toLocalDatetimeInput(new Date()) : (entry.actualClockOut ? toLocalDatetimeInput(entry.actualClockOut) : ''));
    setCorrectionReason('');
  };

  const handleSubmitCorrection = async () => {
    if (!correctionModal || !correctionClockIn || !correctionReason.trim()) {
      showToast('Please fill in all fields including correction reason', 'error');
      return;
    }
    const clockIn = new Date(correctionClockIn);
    const clockOut = correctionClockOut ? new Date(correctionClockOut) : null;
    if (clockOut && clockOut <= clockIn) {
      showToast('Clock-out time must be after clock-in time', 'error');
      return;
    }
    if (!clockOut && !correctionModal.isActive) {
      showToast('Clock-out time is required', 'error');
      return;
    }
    setCorrectionSubmitting(true);
    try {
      await correctTimeEntry(correctionModal.entryId, clockIn, clockOut, correctionReason.trim());
      setCorrectionModal(null);
    } catch (error) {
      console.error('Error correcting entry:', error);
      showToast('Failed to correct time entry', 'error');
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const toggleEntrySelection = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) newSelected.delete(entryId);
    else newSelected.add(entryId);
    setSelectedEntries(newSelected);
  };

  const selectAll = () => {
    if (selectedEntries.size === filteredPendingEntries.length) setSelectedEntries(new Set());
    else setSelectedEntries(new Set(filteredPendingEntries.map(e => e.id)));
  };

  const staffMap = useMemo(() => new Map(staff.map((s) => [s.uid, s])), [staff]);

  const filteredPendingEntries = useMemo(() => {
    if (!searchTerm) return pendingEntries;
    return pendingEntries.filter((entry) => {
      const staffMember = staffMap.get(entry.userId);
      const staffName = `${staffMember?.firstName || ''} ${staffMember?.lastName || ''}`.toLowerCase();
      return staffName.includes(searchTerm.toLowerCase());
    });
  }, [pendingEntries, searchTerm, staffMap]);

  const filteredActiveEntries = useMemo(() => {
    if (!searchTerm) return activeEntries;
    return activeEntries.filter((entry) => {
      const staffMember = staffMap.get(entry.userId);
      const staffName = `${staffMember?.firstName || ''} ${staffMember?.lastName || ''}`.toLowerCase();
      return staffName.includes(searchTerm.toLowerCase());
    });
  }, [activeEntries, searchTerm, staffMap]);

  const gracePeriodHours = gracePeriodMinutes / 60;
  const stats = {
    activeSessions: activeEntries.length,
    totalPending: pendingEntries.length,
    lateClockIns: pendingEntries.filter(e => (e.clockInVariance || 0) > gracePeriodMinutes).length,
    shortHours: pendingEntries.filter(e =>
      e.scheduledHours != null && e.actualHours != null && e.actualHours < e.scheduledHours - gracePeriodHours
    ).length,
  };

  if (loading) return <LoadingState message="Loading pending approvals..." />;

  const renderActiveSection = () => {
    if (filteredActiveEntries.length === 0) return null;
    return (
      <div style={{ marginBottom: SPACING['2xl'] }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.lg }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: COLORS.danger.main, boxShadow: `0 0 0 3px ${COLORS.danger.light}` }} />
          <h2 style={{ fontSize: TYPOGRAPHY.fontSize.lg, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.danger.dark, margin: 0 }}>
            Currently Clocked In — No Clock-Out ({filteredActiveEntries.length})
          </h2>
        </div>
        <div style={{ backgroundColor: COLORS.background.primary, borderRadius: BORDER_RADIUS.xl, border: `1px solid ${COLORS.danger.main}`, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: COLORS.danger.light }}>
              <tr>
                {['Staff Member', 'Job', 'Clocked In', 'Duration', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: SPACING.lg, textAlign: 'left', fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.danger.dark }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredActiveEntries.map((entry) => {
                const staffMember = staffMap.get(entry.userId);
                const durationMs = Date.now() - entry.actualClockIn.getTime();
                const durationH = Math.floor(durationMs / 3600000);
                const durationM = Math.floor((durationMs % 3600000) / 60000);
                const isStuck = durationMs > 12 * 3600000;
                return (
                  <tr key={entry.id} style={{ borderTop: `1px solid ${COLORS.border.light}` }}>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary }}>
                      {staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'Unknown'}
                    </td>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                      {(entry as TimeEntry & { jobTitle?: string }).jobTitle || '—'}
                    </td>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                      {entry.actualClockIn.toLocaleDateString("en-IE", { ...(tz && { timeZone: tz }) })} {entry.actualClockIn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, ...(tz && { timeZone: tz }) })}
                    </td>
                    <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm }}>
                      <span style={{ color: isStuck ? COLORS.danger.main : COLORS.text.secondary, fontWeight: isStuck ? TYPOGRAPHY.fontWeight.semibold : TYPOGRAPHY.fontWeight.normal }}>
                        {durationH}h {durationM}m{isStuck ? ' ⚠' : ''}
                      </span>
                    </td>
                    <td style={{ padding: SPACING.lg }}>
                      <button
                        onClick={() => openCorrectionModal(entry, true)}
                        style={{ padding: `${SPACING.xs} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.xs, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.inverse, backgroundColor: COLORS.danger.main, border: 'none', borderRadius: BORDER_RADIUS.sm, cursor: 'pointer' }}
                      >
                        Force Clock-Out
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: SPACING['2xl'] }}>
      <PageHeader
        title="Approvals"
        subtitle="Action items that need your attention — force clock-outs and pending approvals"
      />

      {/* Stats Cards */}
      <DashboardGrid columns={4}>
        <StatCard
          title="Active Sessions"
          value={stats.activeSessions}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.danger.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><circle cx="20" cy="6" r="2" fill={COLORS.danger.dark} stroke="none"/></svg>}
          color={COLORS.danger.light}
          subtitle="No clock-out yet"
        />
        <StatCard
          title="Pending Approval"
          value={stats.totalPending}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.warning.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>}
          color={COLORS.warning.light}
          subtitle="Awaiting review"
        />
        <StatCard
          title="Late Clock-Ins"
          value={stats.lateClockIns}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.warning.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M5 3L2 6"/><path d="M22 6l-3-3"/></svg>}
          color={COLORS.warning.light}
          subtitle={`>${gracePeriodMinutes}min late`}
        />
        <StatCard
          title="Short Hours"
          value={stats.shortHours}
          icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.danger.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/><line x1="8" y1="18" x2="16" y2="18"/></svg>}
          color={COLORS.danger.light}
          subtitle="Worked short hours"
        />
      </DashboardGrid>

      {/* Search + Select All */}
      <div style={{ marginTop: SPACING['2xl'], marginBottom: SPACING.xl, display: 'flex', gap: SPACING.md, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search by staff name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: '1', minWidth: '250px', padding: `${SPACING.sm} ${SPACING.lg}`, fontSize: TYPOGRAPHY.fontSize.sm, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, outline: 'none' }}
          onFocus={(e) => { e.currentTarget.style.borderColor = COLORS.primary.main; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = COLORS.border.medium; }}
        />
        {filteredPendingEntries.length > 0 && (
          <button
            onClick={selectAll}
            style={{ padding: `${SPACING.sm} ${SPACING.lg}`, fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary, backgroundColor: COLORS.background.secondary, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, cursor: 'pointer' }}
          >
            {selectedEntries.size === filteredPendingEntries.length ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>

      {/* Active Sessions */}
      {renderActiveSection()}

      {/* Bulk Actions Bar */}
      {selectedEntries.size > 0 && (
        <div style={{ backgroundColor: COLORS.primary.light, padding: SPACING.lg, borderRadius: BORDER_RADIUS.md, marginBottom: SPACING.lg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.primary.dark }}>
            {selectedEntries.size} {selectedEntries.size === 1 ? 'entry' : 'entries'} selected
          </span>
          <QuickActionButton
            label="Approve Selected"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
            onClick={openBulkApproveConfirm}
            variant="primary"
          />
        </div>
      )}

      {/* Pending Entries */}
      <div style={{ marginBottom: SPACING.lg }}>
        <h2 style={{ fontSize: TYPOGRAPHY.fontSize.lg, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.primary, marginBottom: SPACING.lg }}>
          Pending Approval
        </h2>
        <div style={{ backgroundColor: COLORS.background.primary, borderRadius: BORDER_RADIUS.xl, border: `1px solid ${COLORS.border.light}`, overflow: 'hidden' }}>
          {filteredPendingEntries.length === 0 ? (
            <div style={{ padding: SPACING['3xl'], textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: SPACING.md }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: COLORS.success.light, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.success.dark }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <p style={{ fontSize: TYPOGRAPHY.fontSize.lg, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.primary }}>
                {searchTerm ? 'No matching entries found' : 'No pending approvals'}
              </p>
              <p style={{ fontSize: TYPOGRAPHY.fontSize.base, color: COLORS.text.secondary }}>
                {searchTerm ? 'Try a different search term' : 'All time entries have been reviewed. Great job!'}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: COLORS.background.secondary }}>
                <tr>
                  <th style={{ padding: SPACING.lg, width: '40px' }}>
                    <input type="checkbox" checked={selectedEntries.size === filteredPendingEntries.length && filteredPendingEntries.length > 0} onChange={selectAll} style={{ cursor: 'pointer' }} />
                  </th>
                  {['Staff Member', 'Job', 'Date', 'Clock In', 'Clock Out', 'Hours', 'Flags', 'Correction Note', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: SPACING.lg, textAlign: 'left', fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.semibold, color: COLORS.text.secondary }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPendingEntries.map((entry) => {
                  const staffMember = staffMap.get(entry.userId);
                  const isLate = (entry.clockInVariance || 0) > gracePeriodMinutes;
                  const isEarly = (entry.clockOutVariance || 0) < -gracePeriodMinutes;
                  const isShortHours = entry.scheduledHours != null && entry.actualHours != null && entry.actualHours < entry.scheduledHours - gracePeriodHours;
                  return (
                    <tr key={entry.id} style={{ borderTop: `1px solid ${COLORS.border.light}` }}>
                      <td style={{ padding: SPACING.lg }}>
                        <input type="checkbox" checked={selectedEntries.has(entry.id)} onChange={() => toggleEntrySelection(entry.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary }}>
                        {staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : 'Unknown'}
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                        {(entry as TimeEntry & { jobTitle?: string }).jobTitle || '—'}
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                        {entry.actualClockIn.toLocaleDateString("en-IE", { ...(tz && { timeZone: tz }) })}
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: isLate ? COLORS.danger.main : COLORS.text.secondary }}>
                        {entry.actualClockIn.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, ...(tz && { timeZone: tz }) })}
                        {(entry.clockInVariance || 0) !== 0 && (
                          <span style={{ marginLeft: SPACING.xs, fontSize: TYPOGRAPHY.fontSize.xs, color: isLate ? COLORS.danger.main : COLORS.text.tertiary }}>
                            ({(entry.clockInVariance || 0) > 0 ? '+' : ''}{Math.round(entry.clockInVariance || 0)}min)
                          </span>
                        )}
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: isEarly ? COLORS.danger.main : COLORS.text.secondary }}>
                        {entry.actualClockOut ? entry.actualClockOut.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, ...(tz && { timeZone: tz }) }) : '—'}
                        {entry.actualClockOut && (entry.clockOutVariance || 0) !== 0 && (
                          <span style={{ marginLeft: SPACING.xs, fontSize: TYPOGRAPHY.fontSize.xs, color: isEarly ? COLORS.danger.main : COLORS.text.tertiary }}>
                            ({(entry.clockOutVariance || 0) > 0 ? '+' : ''}{Math.round(entry.clockOutVariance || 0)}min)
                          </span>
                        )}
                      </td>
                      <td style={{ padding: SPACING.lg, fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary }}>
                        {entry.paidHours.toFixed(2)}h
                      </td>
                      <td style={{ padding: SPACING.lg }}>
                        <div style={{ display: 'flex', gap: SPACING.xs, flexWrap: 'wrap' }}>
                          {isLate && <Flag label="Late arrival" color={COLORS.warning} />}
                          {isEarly && <Flag label="Early departure" color={COLORS.warning} />}
                          {isShortHours && <Flag label="Short hours" color={COLORS.danger} />}
                          {!isLate && !isEarly && !isShortHours && <span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: COLORS.text.secondary }}>On time</span>}
                        </div>
                      </td>
                      <td style={{ padding: SPACING.lg, maxWidth: 160 }}>
                        {(entry as TimeEntry & { managerAdjustments?: { correctionReason?: string } }).managerAdjustments?.correctionReason
                          ? <span title={(entry as TimeEntry & { managerAdjustments?: { correctionReason?: string } }).managerAdjustments!.correctionReason} style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: COLORS.text.secondary, fontStyle: 'italic', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {(entry as TimeEntry & { managerAdjustments?: { correctionReason?: string } }).managerAdjustments!.correctionReason}
                            </span>
                          : <span style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: COLORS.text.tertiary }}>—</span>
                        }
                      </td>
                      <td style={{ padding: SPACING.lg }}>
                        <div style={{ display: 'flex', gap: SPACING.sm }}>
                          <button
                            onClick={() => openApproveConfirm(entry)}
                            style={{ padding: `${SPACING.xs} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.xs, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.inverse, backgroundColor: COLORS.success.main, border: 'none', borderRadius: BORDER_RADIUS.sm, cursor: 'pointer' }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => openCorrectionModal(entry, false)}
                            style={{ padding: `${SPACING.xs} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.xs, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.warning.dark, backgroundColor: COLORS.warning.light, border: `1px solid ${COLORS.warning.main}`, borderRadius: BORDER_RADIUS.sm, cursor: 'pointer' }}
                          >
                            Correct
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Single Approve Confirm Dialog */}
      {approveConfirm && (
        <Modal>
          <h2 style={{ fontSize: TYPOGRAPHY.fontSize.xl, fontWeight: TYPOGRAPHY.fontWeight.bold, color: COLORS.text.primary, marginBottom: SPACING.sm }}>
            Approve Time Entry?
          </h2>
          <p style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary, marginBottom: SPACING.xl }}>
            Approve <strong>{approveConfirm.staffName}</strong>'s entry of <strong>{approveConfirm.hours.toFixed(2)} hrs</strong>? This will lock the paid hours.
          </p>
          <ModalButtons onCancel={() => setApproveConfirm(null)} onConfirm={confirmApproveEntry} confirmLabel="Approve" confirmColor={COLORS.success.main} />
        </Modal>
      )}

      {/* Bulk Approve Confirm Dialog */}
      {bulkApproveConfirm && (
        <Modal>
          <h2 style={{ fontSize: TYPOGRAPHY.fontSize.xl, fontWeight: TYPOGRAPHY.fontWeight.bold, color: COLORS.text.primary, marginBottom: SPACING.sm }}>
            Approve {bulkApproveConfirm.entryIds.length} {bulkApproveConfirm.entryIds.length === 1 ? 'Entry' : 'Entries'}?
          </h2>
          <p style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary, marginBottom: SPACING.xl }}>
            Total: <strong>{bulkApproveConfirm.totalHours.toFixed(2)} hrs</strong> across <strong>{bulkApproveConfirm.entryIds.length}</strong> {bulkApproveConfirm.entryIds.length === 1 ? 'entry' : 'entries'}. Paid hours will be locked for all selected entries.
          </p>
          <ModalButtons onCancel={() => setBulkApproveConfirm(null)} onConfirm={confirmBulkApprove} confirmLabel="Approve All" confirmColor={COLORS.success.main} />
        </Modal>
      )}

      {/* Correction / Force Clock-Out Modal */}
      {correctionModal && (
        <Modal width={500}>
          <h2 style={{ fontSize: TYPOGRAPHY.fontSize.xl, fontWeight: TYPOGRAPHY.fontWeight.bold, color: COLORS.text.primary, marginBottom: SPACING.xs }}>
            {correctionModal.isActive ? 'Force Clock-Out' : 'Correct Time Entry'}
          </h2>
          <p style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.secondary, marginBottom: SPACING.xl }}>
            {correctionModal.staffName}
            {correctionModal.isActive && (
              <span style={{ marginLeft: SPACING.sm, padding: `2px ${SPACING.sm}`, borderRadius: BORDER_RADIUS.sm, backgroundColor: COLORS.danger.light, color: COLORS.danger.dark, fontSize: TYPOGRAPHY.fontSize.xs }}>
                Currently clocked in
              </span>
            )}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.lg }}>
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary, marginBottom: SPACING.xs }}>
                {correctionModal.isActive ? 'Clock In (original)' : 'Corrected Clock In'}
              </label>
              <input type="datetime-local" value={correctionClockIn} onChange={(e) => setCorrectionClockIn(e.target.value)} style={{ width: '100%', padding: `${SPACING.sm} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.sm, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary, marginBottom: SPACING.xs }}>
                {correctionModal.isActive ? 'Clock Out (set now)' : 'Corrected Clock Out'}
                {correctionModal.isActive && <span style={{ color: COLORS.danger.main, marginLeft: 4 }}>*</span>}
              </label>
              <input type="datetime-local" value={correctionClockOut} onChange={(e) => setCorrectionClockOut(e.target.value)} style={{ width: '100%', padding: `${SPACING.sm} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.sm, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, outline: 'none', boxSizing: 'border-box' }} />
              {correctionModal.isActive && <p style={{ fontSize: TYPOGRAPHY.fontSize.xs, color: COLORS.text.tertiary, marginTop: SPACING.xs }}>Entry will move to Pending Approval after force clock-out.</p>}
            </div>
            <div>
              <label style={{ display: 'block', fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary, marginBottom: SPACING.xs }}>
                Reason <span style={{ color: COLORS.danger.main }}>*</span>
              </label>
              <textarea value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} placeholder={correctionModal.isActive ? 'e.g. Staff forgot to clock out' : 'Explain why these times are being corrected...'} rows={3} style={{ width: '100%', padding: `${SPACING.sm} ${SPACING.md}`, fontSize: TYPOGRAPHY.fontSize.sm, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
          </div>
          <ModalButtons onCancel={() => setCorrectionModal(null)} onConfirm={handleSubmitCorrection} confirmLabel={correctionSubmitting ? 'Saving...' : correctionModal.isActive ? 'Force Clock-Out' : 'Save Correction'} confirmColor={correctionModal.isActive ? COLORS.danger.main : COLORS.primary.main} confirmDisabled={correctionSubmitting} cancelDisabled={correctionSubmitting} />
        </Modal>
      )}

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

// ── Small local helpers ──────────────────────────────────────────────────────

function Flag({ label, color }: { label: string; color: { light: string; dark: string } }) {
  return (
    <span style={{ padding: `${SPACING.xs} ${SPACING.sm}`, borderRadius: BORDER_RADIUS.sm, fontSize: TYPOGRAPHY.fontSize.xs, fontWeight: TYPOGRAPHY.fontWeight.medium, backgroundColor: color.light, color: color.dark }}>
      {label}
    </span>
  );
}

function Modal({ children, width = 400 }: { children: React.ReactNode; width?: number }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: COLORS.background.primary, borderRadius: BORDER_RADIUS.xl, padding: SPACING['2xl'], width, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {children}
      </div>
    </div>
  );
}

function ModalButtons({ onCancel, onConfirm, confirmLabel, confirmColor, confirmDisabled = false, cancelDisabled = false }: { onCancel: () => void; onConfirm: () => void; confirmLabel: string; confirmColor: string; confirmDisabled?: boolean; cancelDisabled?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: SPACING.md, marginTop: SPACING.xl, justifyContent: 'flex-end' }}>
      <button onClick={onCancel} disabled={cancelDisabled} style={{ padding: `${SPACING.sm} ${SPACING.xl}`, fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.primary, backgroundColor: COLORS.background.secondary, border: `1px solid ${COLORS.border.medium}`, borderRadius: BORDER_RADIUS.md, cursor: cancelDisabled ? 'not-allowed' : 'pointer' }}>
        Cancel
      </button>
      <button onClick={onConfirm} disabled={confirmDisabled} style={{ padding: `${SPACING.sm} ${SPACING.xl}`, fontSize: TYPOGRAPHY.fontSize.sm, fontWeight: TYPOGRAPHY.fontWeight.medium, color: COLORS.text.inverse, backgroundColor: confirmDisabled ? COLORS.text.tertiary : confirmColor, border: 'none', borderRadius: BORDER_RADIUS.md, cursor: confirmDisabled ? 'not-allowed' : 'pointer' }}>
        {confirmLabel}
      </button>
    </div>
  );
}
